// ============================================================================
// meteoraTrade — "Order venue: Meteora" (orders-venue-20261016)
// ----------------------------------------------------------------------------
// Market:  direct swap on the DEEPEST SOL-paired Meteora pool for the coin
//          (DLMM or DAMM v2, whichever has more TVL), using the slippage
//          setting. No Jupiter, no routing.
// Limit:   native DLMM limit orders (place_limit_order / cancel_limit_order /
//          close_limit_order_if_empty) on the deepest SOL-paired DLMM pool
//          that supports them. Needs @meteora-ag/dlmm >= 1.9.14 (1.9.6 has no
//          limit-order methods) — we throw a clear error instead of guessing.
// Never falls back to Jupiter: every "can't do it here" path throws a
// message the toast shows as-is.
// Pool discovery reuses meteoraLp.js (findLpPools / findDlmmPoolsRest);
// sending reuses its sendV2Tx (simulate → sign → send → confirm).
// ============================================================================

import { findLpPools, findDlmmPoolsRest, _v2deps, _dlmmDeps, _sendV2Tx, _mintInfo } from './meteoraLp'
import { getTokenBalanceRaw, getMintSupplyInfo } from './jupiter'

const WSOL = 'So11111111111111111111111111111111111111112'
const POOL_TTL_MS = 60_000

const _swapPoolCache = new Map()   // mint -> { at, row }
const _loPoolCache = new Map()     // mint -> { at, pool }

function inflightError(msg, signature) {
  const e = new Error(msg)
  e.inflight = true
  e.signature = signature
  return e
}

/** Turn sendV2Tx's { signature, state } into the same contract executeBuy/
 *  executeSell have: return on confirmed, throw err.inflight when it MAY have
 *  landed, throw plainly when it definitely did not. */
function settle(res, what) {
  if (res.state === 'confirmed') return res.signature
  if (res.state === 'expired') throw new Error(`${what} expired before landing (not executed)`)
  throw inflightError(`${what} submitted but not confirmed yet`, res.signature)
}

// ─── Market ───────────────────────────────────────────────────────────

/** Deepest SOL-paired Meteora pool (DLMM or DAMM v2) for `mint`. */
export async function findMeteoraSwapPool(mint) {
  const hit = _swapPoolCache.get(mint)
  if (hit && Date.now() - hit.at < POOL_TTL_MS) return hit.row
  const { rows } = await findLpPools(mint, { limit: 50 })
  const row = rows.filter(r => r.pairIsSol && !r.unindexed).sort((a, b) => b.tvl - a.tvl)[0] || null
  if (!row) throw new Error('Meteora: no SOL-paired Meteora pool with liquidity for this coin')
  _swapPoolCache.set(mint, { at: Date.now(), row })
  return row
}

async function swapOnPool({ row, inMint, outMint, inAmountRaw, owner, signTransaction, slippageBps, onSent }) {
  const bps = Math.max(1, Math.min(5000, Math.round(Number(slippageBps) || 100)))
  if (row.type === 'dlmm') {
    const { DLMM, connection, web3, BN } = await _dlmmDeps()
    const dlmm = await DLMM.create(connection, new web3.PublicKey(row.pool))
    const swapForY = dlmm.tokenX.publicKey.toBase58() === inMint
    const binArrays = await dlmm.getBinArrayForSwap(swapForY, 4)
    const quote = dlmm.swapQuote(new BN(String(inAmountRaw)), swapForY, new BN(bps), binArrays)
    if (quote.outAmount.isZero()) throw new Error('Meteora: pool cannot fill this size')
    const tx = await dlmm.swap({
      inToken: new web3.PublicKey(inMint), outToken: new web3.PublicKey(outMint),
      inAmount: quote.consumedInAmount, minOutAmount: quote.minOutAmount,
      lbPair: dlmm.pubkey, user: new web3.PublicKey(String(owner)),
      binArraysPubkey: quote.binArraysPubkey,
    })
    // The on-chain DLMM program (limit-order release) now requires the bin
    // array bitmap extension to be WRITABLE in swap/swap2. SDK < 1.9.14 marks
    // it read-only -> Anchor 2000 ConstraintMut. Pools without an extension
    // pass the program id placeholder and are unaffected, so only flip the
    // real extension account. Harmless once the SDK is upgraded.
    const extPk = dlmm.binArrayBitmapExtension?.publicKey
    if (extPk) {
      const progId = dlmm.program?.programId?.toBase58?.()
      for (const ix of tx.instructions) {
        if (progId && ix.programId.toBase58() !== progId) continue
        for (const k of ix.keys) if (k.pubkey.equals(extPk)) k.isWritable = true
      }
    }
    console.log(`[MeteoraSwap] DLMM ${row.pool.slice(0, 6)} in=${quote.consumedInAmount} out≈${quote.outAmount} min=${quote.minOutAmount} ext=${extPk ? 'yes' : 'no'}`)
    return _sendV2Tx({ instructions: tx.instructions, owner, signTransaction,
      onStatus: (s) => { if (s === 'confirming') onSent?.() } })
  }
  // DAMM v2
  const { cpAmm, connection, web3, BN } = await _v2deps()
  const poolPk = new web3.PublicKey(row.pool)
  const st = await cpAmm.fetchPoolState(poolPk)
  const [a, b] = await _mintInfo([st.tokenAMint.toBase58(), st.tokenBMint.toBase58()])
  const slot = await connection.getSlot('confirmed')
  const time = await connection.getBlockTime(slot).catch(() => null) || Math.floor(Date.now() / 1000)
  const q = cpAmm.getQuote({
    inAmount: new BN(String(inAmountRaw)), inputTokenMint: new web3.PublicKey(inMint),
    slippage: bps, poolState: st, currentTime: time, currentSlot: slot,
    tokenADecimal: a.decimals, tokenBDecimal: b.decimals,
  })
  if (q.swapOutAmount.isZero()) throw new Error('Meteora: pool cannot fill this size')
  const tx = await cpAmm.swap({
    payer: new web3.PublicKey(String(owner)), pool: poolPk,
    inputTokenMint: new web3.PublicKey(inMint), outputTokenMint: new web3.PublicKey(outMint),
    amountIn: new BN(String(inAmountRaw)), minimumAmountOut: q.minSwapOutAmount,
    tokenAMint: st.tokenAMint, tokenBMint: st.tokenBMint,
    tokenAVault: st.tokenAVault, tokenBVault: st.tokenBVault,
    tokenAProgram: a.program, tokenBProgram: b.program,
    referralTokenAccount: null, poolState: st,
  })
  console.log(`[MeteoraSwap] DAMMv2 ${row.pool.slice(0, 6)} out≈${q.swapOutAmount} min=${q.minSwapOutAmount}`)
  return _sendV2Tx({ instructions: tx.instructions, owner, signTransaction,
    onStatus: (s) => { if (s === 'confirming') onSent?.() } })
}

function cleanSwapError(e, row) {
  const m = String(e?.message || e)
  if (/Custom":2000|0x7d0/.test(m)) return new Error(`Meteora ${row.type.toUpperCase()} swap rejected (account mutability, SDK too old) — update @meteora-ag/dlmm to 1.9.14`)
  if (/Custom":6003|ExceededAmountSlippageTolerance|0x1773/.test(m)) return new Error('Meteora swap: price moved past slippage')
  return e
}

/** Market buy `solAmount` SOL of `mint`. Returns { txid, pool }. */
export async function meteoraBuy(mint, solAmount, owner, signTransaction, slippageBps, onSent) {
  const lamports = Math.floor(Number(solAmount) * 1e9)
  if (!(lamports > 0)) throw new Error('Buy amount must be > 0')
  const row = await findMeteoraSwapPool(mint)
  const res = await swapOnPool({ row, inMint: WSOL, outMint: mint, inAmountRaw: lamports, owner, signTransaction, slippageBps, onSent }).catch(e => { throw cleanSwapError(e, row) })
  return { txid: settle(res, 'Meteora buy'), pool: row.pool, poolType: row.type }
}

/** Market sell `percentage`% of the wallet's `mint`. Returns { txid, pool }. */
export async function meteoraSell(mint, percentage, owner, signTransaction, slippageBps, onSent) {
  const bal = await getTokenBalanceRaw(owner, mint)
  if (!bal) throw new Error('No tokens to sell')
  const amt = Math.floor(bal * (Math.min(100, Number(percentage)) / 100))
  if (!amt) throw new Error('Sell amount too small')
  const row = await findMeteoraSwapPool(mint)
  const res = await swapOnPool({ row, inMint: mint, outMint: WSOL, inAmountRaw: amt, owner, signTransaction, slippageBps, onSent }).catch(e => { throw cleanSwapError(e, row) })
  return { txid: settle(res, 'Meteora sell'), pool: row.pool, poolType: row.type }
}

// ─── Limit (native DLMM limit orders) ─────────────────────────────────

async function sdkLimitSupport() {
  const mod = await import('@meteora-ag/dlmm')
  const isSupportLimitOrder = mod.isSupportLimitOrder || mod.default?.isSupportLimitOrder
  return { isSupportLimitOrder, LimitOrderStatus: mod.LimitOrderStatus || mod.default?.LimitOrderStatus || { NotFilled: 0, PartialFilled: 1, Fulfilled: 2 } }
}

/** Deepest SOL-paired DLMM pool that accepts limit orders. Returns a loaded
 *  DLMM instance. Throws a user-facing message when none / SDK too old. */
export async function findMeteoraLimitPool(mint) {
  const { DLMM, connection, web3 } = await _dlmmDeps()
  const { isSupportLimitOrder } = await sdkLimitSupport()
  const hit = _loPoolCache.get(mint)
  if (hit && Date.now() - hit.at < POOL_TTL_MS) {
    const dlmm = await DLMM.create(connection, new web3.PublicKey(hit.pool))
    return dlmm
  }
  const rows = (await findDlmmPoolsRest(mint)).filter(r => r.pairIsSol && r.tvl > 0).sort((a, b) => b.tvl - a.tvl)
  if (!rows.length) throw new Error('Meteora: no SOL-paired DLMM pool for this coin (limit orders need DLMM)')
  for (const r of rows.slice(0, 5)) {
    const dlmm = await DLMM.create(connection, new web3.PublicKey(r.pool))
    if (typeof dlmm.placeLimitOrder !== 'function' || typeof isSupportLimitOrder !== 'function') {
      throw new Error('Meteora limit orders need @meteora-ag/dlmm 1.9.14+ (installed SDK is older) — run: npm i @meteora-ag/dlmm@1.9.14')
    }
    let ok = false
    try { ok = isSupportLimitOrder(dlmm.lbPair) } catch { ok = false }
    if (ok) { _loPoolCache.set(mint, { at: Date.now(), pool: r.pool }); return dlmm }
  }
  throw new Error('Meteora: this coin\'s DLMM pools don\'t accept limit orders (liquidity-mining pools)')
}

/** UI price of a bin as SOL per token. */
function binToSolPerToken(dlmm, binId, tokenIsX, getPriceOfBinByBinId) {
  const perLamport = Number(getPriceOfBinByBinId(binId, Number(dlmm.lbPair.binStep)))
  const yPerX = Number(dlmm.fromPricePerLamport(perLamport))
  return tokenIsX ? yPerX : (yPerX > 0 ? 1 / yPerX : 0)
}

/**
 * Place a native DLMM limit order at `targetMcapSol` (same SOL-mcap units the
 * chart uses). side 'buy' spends `solAmount`; side 'sell' sells `pct`% of the
 * wallet (or exactly `rawAmount` if given).
 * Returns { orderId, pool, binId, isAskSide, targetMcap (snapped to bin), rawAmount, signature }.
 */
export async function placeMeteoraLimit({ mint, side, solAmount, pct, rawAmount, targetMcapSol, owner, signTransaction }) {
  const { web3, BN } = await _dlmmDeps()
  const mod = await import('@meteora-ag/dlmm')
  const getPriceOfBinByBinId = mod.getPriceOfBinByBinId || mod.default?.getPriceOfBinByBinId
  const dlmm = await findMeteoraLimitPool(mint)
  await dlmm.refetchStates()
  const { supplyUi } = await getMintSupplyInfo(mint)
  const solPerToken = targetMcapSol / supplyUi
  if (!(solPerToken > 0)) throw new Error('Bad target price')

  const tokenIsX = dlmm.tokenX.publicKey.toBase58() === mint
  const yPerX = tokenIsX ? solPerToken : 1 / solPerToken
  // Selling X (= ask) when we give X; bid when we give Y.
  const givingToken = side === 'sell'
  const givingX = givingToken ? tokenIsX : !tokenIsX
  const isAskSide = givingX
  // Ask rounds UP (never sell below target), bid rounds DOWN (never buy above).
  const binId = dlmm.getBinIdFromPrice(Number(dlmm.toPricePerLamport(yPerX)), !isAskSide)
  const activeId = dlmm.lbPair.activeId
  if (isAskSide ? binId <= activeId : binId >= activeId) {
    throw new Error(`Target is at/through the current price — it would fill now. Use a market order (or move the target ${side === 'buy' ? 'below' : 'above'} price).`)
  }

  let amount
  if (side === 'buy') {
    amount = Math.floor(Number(solAmount) * 1e9)
    if (!(amount > 0)) throw new Error('Set buy amount first')
  } else if (rawAmount) {
    amount = Math.floor(Number(rawAmount))
  } else {
    const bal = await getTokenBalanceRaw(owner, mint)
    amount = Math.floor(bal * (Math.min(100, Number(pct) || 100) / 100))
    if (!amount) throw new Error('No tokens to sell')
  }

  const ownerPk = new web3.PublicKey(String(owner))
  const kp = web3.Keypair.generate()
  const tx = await dlmm.placeLimitOrder({
    owner: ownerPk, payer: ownerPk, sender: ownerPk, limitOrder: kp.publicKey,
    params: { isAskSide, relativeBin: null, bins: [{ id: binId, amount: new BN(String(amount)) }] },
  })
  const res = await _sendV2Tx({ instructions: tx.instructions, owner, signTransaction, extraSigners: [kp] })
  if (res.state !== 'confirmed') {
    if (res.state === 'expired') throw new Error('Meteora limit order expired before landing (not placed)')
    throw inflightError('Meteora limit order submitted but not confirmed yet — check open orders', res.signature)
  }
  const snapped = binToSolPerToken(dlmm, binId, tokenIsX, getPriceOfBinByBinId) * supplyUi
  console.log(`[MeteoraLO] placed ${side} bin=${binId} (active ${activeId}) ask=${isAskSide} pool=${dlmm.pubkey.toBase58().slice(0, 6)}`)
  return {
    orderId: kp.publicKey.toBase58(), pool: dlmm.pubkey.toBase58(), binId, isAskSide,
    targetMcap: snapped > 0 ? snapped : targetMcapSol, rawAmount: side === 'sell' ? amount : null,
    signature: res.signature,
  }
}

/** Status of one Meteora limit order:
 *  'open' | 'partial' | 'filled' | 'closed' (account gone). */
export async function getMeteoraLimitStatus({ pool, orderId }) {
  const { DLMM, connection, web3 } = await _dlmmDeps()
  const { LimitOrderStatus } = await sdkLimitSupport()
  const pk = new web3.PublicKey(orderId)
  const info = await connection.getAccountInfo(pk)
  if (!info) return { status: 'closed' }
  const dlmm = await DLMM.create(connection, new web3.PublicKey(pool))
  const lo = await dlmm.getLimitOrder(pk)
  const bins = lo?.limitOrderData?.limitOrderBinData || []
  const live = bins.filter(b => !b.empty)
  if (!live.length) return { status: 'filled', bins }
  if (live.every(b => b.status === LimitOrderStatus.Fulfilled)) return { status: 'filled', bins }
  if (live.some(b => b.status === LimitOrderStatus.PartialFilled || b.status === LimitOrderStatus.Fulfilled)) return { status: 'partial', bins }
  return { status: 'open', bins }
}

/** Cancel (withdraws unfilled + filled proceeds to the wallet), then close
 *  the empty account to reclaim rent. Close failing is non-fatal. */
export async function cancelMeteoraLimit({ pool, orderId, binIds, owner, signTransaction }) {
  const { DLMM, connection, web3 } = await _dlmmDeps()
  const dlmm = await DLMM.create(connection, new web3.PublicKey(pool))
  const pk = new web3.PublicKey(orderId)
  const ownerPk = new web3.PublicKey(String(owner))
  let ids = binIds
  if (!ids?.length) {
    const lo = await dlmm.getLimitOrder(pk)
    ids = (lo?.limitOrderData?.limitOrderBinData || []).filter(b => !b.empty).map(b => b.binId)
  }
  let signature = null
  if (ids.length) {
    const tx = await dlmm.cancelLimitOrder({ limitOrderPubkey: pk, owner: ownerPk, rentReceiver: ownerPk, binIds: ids })
    const res = await _sendV2Tx({ instructions: tx.instructions, owner, signTransaction })
    if (res.state !== 'confirmed') throw new Error(`Cancel not confirmed (${res.state}) — check again before re-placing`)
    signature = res.signature
  }
  try {
    if (await connection.getAccountInfo(pk)) {
      const c = await dlmm.closeLimitOrderIfEmpty({ limitOrder: pk, owner: ownerPk, rentReceiver: ownerPk })
      await _sendV2Tx({ instructions: c.instructions, owner, signTransaction })
    }
  } catch (e) { console.warn('[MeteoraLO] close-if-empty failed (rent stays in account):', e?.message) }
  return { signature }
}

/** All of the wallet's Meteora limit orders on this coin's limit pool, in
 *  the same shape useTrading keeps limit orders in. Read-only. */
export async function listMeteoraLimitOrders(mint, owner) {
  const { web3 } = await _dlmmDeps()
  const mod = await import('@meteora-ag/dlmm')
  const getPriceOfBinByBinId = mod.getPriceOfBinByBinId || mod.default?.getPriceOfBinByBinId
  const dlmm = await findMeteoraLimitPool(mint)
  const { supplyUi } = await getMintSupplyInfo(mint)
  const tokenIsX = dlmm.tokenX.publicKey.toBase58() === mint
  const list = await dlmm.getLimitOrderByUserAndLbPair(new web3.PublicKey(String(owner)))
  return (list || []).map(o => {
    const bins = (o.limitOrderData?.limitOrderBinData || []).filter(b => !b.empty)
    if (!bins.length) return null
    const b = bins[0]
    const givingX = b.isAskSide
    const side = (givingX === tokenIsX) ? 'sell' : 'buy'
    const solIn = !tokenIsX ? b.depositAmountX : b.depositAmountY
    return {
      orderId: o.publicKey.toBase58(), pool: dlmm.pubkey.toBase58(),
      binIds: bins.map(x => x.binId), isAskSide: b.isAskSide, side,
      targetMcap: binToSolPerToken(dlmm, b.binId, tokenIsX, getPriceOfBinByBinId) * supplyUi,
      amount: side === 'buy' ? Number(solIn) / 1e9 : undefined,
    }
  }).filter(Boolean)
}
